/**
 * 消消看棋盘组件
 * 10x10棋盘，彩色宝石消除游戏
 */

import React from 'react';
import { Gem, Star, Animal, Fruit, Match3Piece, GemColor, StarColor, AnimalColor, FruitType, Match3Mode } from '../utils/match3';
import { Position } from '../types/game';
import { cn } from '../lib/utils';

interface Match3BoardProps {
  board: (Match3Piece | null)[][];
  onGemClick: (position: Position) => void;
  selectedGem: Position | null;
  matchedGems: Position[];
  disabled?: boolean;
  mode?: Match3Mode;
}

const Match3Board: React.FC<Match3BoardProps> = ({
  board,
  onGemClick,
  selectedGem,
  matchedGems,
  disabled = false,
  mode = 'classic'
}) => {
  const getPieceColor = (piece: Match3Piece): string => {
    if (piece.type === 'star') {
      const starColorMap = {
        red: 'bg-gradient-to-br from-red-300 to-red-500 hover:from-red-400 hover:to-red-600',
        blue: 'bg-gradient-to-br from-blue-300 to-blue-500 hover:from-blue-400 hover:to-blue-600',
        yellow: 'bg-gradient-to-br from-yellow-200 to-yellow-400 hover:from-yellow-300 hover:to-yellow-500',
        green: 'bg-gradient-to-br from-green-300 to-green-500 hover:from-green-400 hover:to-green-600',
        purple: 'bg-gradient-to-br from-purple-300 to-purple-500 hover:from-purple-400 hover:to-purple-600',
        pink: 'bg-gradient-to-br from-pink-300 to-pink-500 hover:from-pink-400 hover:to-pink-600'
      };
      return starColorMap[piece.color];
    } else if (piece.type === 'animal') {
      // 小动物类型使用更柔和可爱的颜色
      const animalColorMap = {
        red: 'bg-gradient-to-br from-orange-200 to-orange-400 hover:from-orange-300 hover:to-orange-500', // 小熊
        blue: 'bg-gradient-to-br from-blue-200 to-blue-400 hover:from-blue-300 hover:to-blue-500',   // 小兔
        green: 'bg-gradient-to-br from-green-200 to-green-400 hover:from-green-300 hover:to-green-500', // 青蛙
        yellow: 'bg-gradient-to-br from-yellow-100 to-yellow-300 hover:from-yellow-200 hover:to-yellow-400', // 小鸡
        purple: 'bg-gradient-to-br from-purple-200 to-purple-400 hover:from-purple-300 hover:to-purple-500', // 小狐狸
        orange: 'bg-gradient-to-br from-red-200 to-red-400 hover:from-red-300 hover:to-red-500'   // 小老虎
      };
      return animalColorMap[piece.color];
    } else if (piece.kind === 'fruit') {
      // 水果类型使用可爱的水果颜色
      const fruitColorMap = {
        apple: 'bg-gradient-to-br from-red-200 to-red-400 hover:from-red-300 hover:to-red-500',      // 苹果
        banana: 'bg-gradient-to-br from-yellow-200 to-yellow-400 hover:from-yellow-300 hover:to-yellow-500', // 香蕉
        grape: 'bg-gradient-to-br from-purple-200 to-purple-400 hover:from-purple-300 hover:to-purple-500',   // 葡萄
        orange: 'bg-gradient-to-br from-orange-200 to-orange-400 hover:from-orange-300 hover:to-orange-500', // 橙子
        strawberry: 'bg-gradient-to-br from-pink-200 to-pink-400 hover:from-pink-300 hover:to-pink-500',   // 草莓
        watermelon: 'bg-gradient-to-br from-green-200 to-green-400 hover:from-green-300 hover:to-green-500'  // 西瓜
      };
      return fruitColorMap[piece.type];
    } else {
      const gemColorMap = {
        red: 'bg-gradient-to-br from-red-400 to-red-600 hover:from-red-500 hover:to-red-700',
        blue: 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700',
        green: 'bg-gradient-to-br from-green-400 to-green-600 hover:from-green-500 hover:to-green-700',
        yellow: 'bg-gradient-to-br from-yellow-300 to-yellow-500 hover:from-yellow-400 hover:to-yellow-600',
        purple: 'bg-gradient-to-br from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700',
        orange: 'bg-gradient-to-br from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700'
      };
      return gemColorMap[piece.color];
    }
  };

  const getPieceIcon = (piece: Match3Piece): string => {
    if (piece.type === 'star') {
      const starIconMap = {
        red: '🌟',
        blue: '✨',
        yellow: '⭐',
        green: '💫',
        purple: '🌠',
        pink: '⭕'
      };
      return starIconMap[piece.color];
    } else if (piece.type === 'animal') {
      // 小动物类型使用可爱的小动物图标
      const animalIconMap = {
        red: '🐻', // 小熊
        blue: '🐰', // 小兔
        green: '🐸', // 青蛙
        yellow: '🐥', // 小鸡
        purple: '🦊', // 小狐狸
        orange: '🐯'  // 小老虎
      };
      return animalIconMap[piece.color];
    } else if (piece.kind === 'fruit') {
      // 水果类型使用可爱的小水果图标
      const fruitIconMap = {
        apple: '🍎',      // 苹果
        banana: '🍌',     // 香蕉
        grape: '🍇',      // 葡萄
        orange: '🍊',     // 橙子
        strawberry: '🍓', // 草莓
        watermelon: '🍉'  // 西瓜
      };
      return fruitIconMap[piece.type];
    } else {
      const gemIconMap = {
        red: '🔶',
        blue: '💎',
        green: '🍀',
        yellow: '🌟',
        purple: '💟',
        orange: '💝'
      };
      return gemIconMap[piece.color];
    }
  };

  const isSelected = (row: number, col: number): boolean => {
    return selectedGem?.row === row && selectedGem?.col === col;
  };

  const isMatched = (row: number, col: number): boolean => {
    return matchedGems.some(pos => pos.row === row && pos.col === col);
  };

  const isAdjacentToSelected = (row: number, col: number): boolean => {
    if (!selectedGem || (mode !== 'happy' && mode !== 'fruit')) return false;

    return (
      (Math.abs(selectedGem.row - row) === 1 && selectedGem.col === col) ||
      (Math.abs(selectedGem.col - col) === 1 && selectedGem.row === row)
    );
  };

  return (
    <div className="relative max-w-[700px] max-h-[700px] mx-auto">
      {/* 棋盘背景 */}
      <div className="bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 p-4 sm:p-6 rounded-2xl shadow-2xl border border-purple-200">
        <div
          className="bg-gradient-to-br from-slate-800/10 to-slate-900/20 p-3 sm:p-4 rounded-xl backdrop-blur-sm border border-purple-300/50"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: '3px',
            aspectRatio: '1'
          }}
        >
          {board.map((row, rowIndex) =>
            row.map((piece, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  "relative aspect-square rounded-lg cursor-pointer transition-all duration-300 flex items-center justify-center overflow-hidden",
                  {
                    "ring-3 ring-yellow-400 ring-offset-2": isSelected(rowIndex, colIndex),
                    "ring-2 ring-green-400 ring-offset-1": (mode === 'happy' || mode === 'fruit') && isAdjacentToSelected(rowIndex, colIndex) && piece,
                    "scale-110 animate-pulse": isMatched(rowIndex, colIndex),
                    "cursor-not-allowed": disabled || !piece
                  }
                )}
                onClick={() => piece && !disabled && onGemClick({ row: rowIndex, col: colIndex })}
              >
                {piece && (
                  <div className={cn(
                    "w-full h-full rounded-lg shadow-xl transition-all duration-300 flex items-center justify-center text-2xl sm:text-3xl md:text-4xl border border-white/30",
                    getPieceColor(piece),
                    {
                      "border-2 border-yellow-400 shadow-2xl ring-2 ring-yellow-300": isSelected(rowIndex, colIndex),
                      "animate-spin shadow-2xl": isMatched(rowIndex, colIndex),
                      "hover:shadow-2xl hover:scale-105": !disabled && piece
                    }
                  )}>
                    <span className={cn(
                      "select-none",
                        {
                          "animate-bounce": isSelected(rowIndex, colIndex),
                        }
                      )}>
                      {getPieceIcon(piece)}
                    </span>
                    {/* 添加光泽效果 */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/40 to-transparent rounded-lg opacity-60"></div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Match3Board;